import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, ArrowBigUp, ArrowBigDown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CommentThread({ comments, movieId, onCommentAdded }) {
  const topLevel = comments.filter(c => !c.parent_comment_id);
  
  return (
    <div className="space-y-8" data-testid="comment-thread">
      {topLevel.map(comment => {
        const replies = comments.filter(c => c.parent_comment_id === comment.comment_id);
        const subReplies = replies.map(reply => {
          const sub = comments.filter(c => c.parent_comment_id === reply.comment_id);
          return { ...reply, subs: sub };
        });
        
        return (
          <div key={comment.comment_id} className="relative">
             {replies.length > 0 && (
                <div className="absolute left-5 top-12 bottom-0 w-px bg-border/50" />
             )}
            <CommentBlock
              comment={comment}
              replies={subReplies}
              movieId={movieId}
              onReload={onCommentAdded}
            />
          </div>
        );
      })}
    </div>
  );
}

function CommentBlock({ comment, replies, movieId, onReload }) {
  return (
    <div className="space-y-4">
      <CommentItem comment={comment} movieId={movieId} onReload={onReload} />
      
      {replies && replies.length > 0 && (
        <div className="pl-6 space-y-6 mt-4">
          {replies.map(reply => (
            <div key={reply.comment_id} className="relative">
               {reply.subs && reply.subs.length > 0 && (
                  <div className="absolute left-5 top-12 bottom-0 w-px bg-border/50" />
               )}
              <CommentItem comment={reply} movieId={movieId} onReload={onReload} />
              
              {reply.subs && reply.subs.length > 0 && (
                <div className="pl-6 space-y-4 mt-4">
                  {reply.subs.map(sub => (
                    <CommentItem key={sub.comment_id} comment={sub} movieId={movieId} onReload={onReload} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CommentItem({ comment, movieId, onReload }) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [posting, setPosting] = useState(false);
  
  // Initialize state from props
  const [score, setScore] = useState(comment.score || 0);
  const [voteStatus, setVoteStatus] = useState(comment.vote_status || 'none');

  // CRITICAL FIX: Sync state when props change (e.g. after refresh)
  useEffect(() => {
    setScore(comment.score || 0);
    setVoteStatus(comment.vote_status || 'none');
  }, [comment]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || posting) return;

    setPosting(true);
    try {
      const isThread = typeof movieId === 'string' && isNaN(parseInt(movieId));
      const endpoint = isThread 
        ? `${BACKEND_URL}/api/community/threads/${movieId}/comments`
        : `${BACKEND_URL}/api/movies/${movieId}/comments`;
      
      const payload = isThread
        ? { thread_id: movieId, content: replyText, parent_comment_id: comment.comment_id }
        : { movie_id: parseInt(movieId), content: replyText, parent_comment_id: comment.comment_id };
      
      await axios.post(endpoint, payload, { withCredentials: true });
      setReplyText('');
      setShowReply(false);
      onReload();
      toast.success("Reply posted!");
    } catch (error) {
      toast.error("Failed to post reply");
    } finally {
      setPosting(false);
    }
  };

  const handleVote = async (type) => {
    const previousScore = score;
    const previousStatus = voteStatus;
    
    // Optimistic Calculation
    let newScore = score;
    let newStatus = type;

    if (voteStatus === type) {
      // Toggle off
      newStatus = 'none';
      newScore = type === 'up' ? score - 1 : score + 1;
    } else if (voteStatus === 'none') {
      // New vote
      newScore = type === 'up' ? score + 1 : score - 1;
    } else {
      // Switch vote (e.g. up -> down)
      newScore = type === 'up' ? score + 2 : score - 2;
    }

    setScore(newScore);
    setVoteStatus(newStatus);

    try {
      await axios.post(`${BACKEND_URL}/api/vote`, {
        target_id: comment.comment_id,
        vote_type: type
      }, { withCredentials: true });
    } catch (error) {
      // Revert on failure
      setScore(previousScore);
      setVoteStatus(previousStatus);
      console.error("Vote failed:", error);
      if (error.response?.status === 401) {
        toast.error("Please login to vote");
      } else {
        toast.error("Failed to vote. Please try again.");
      }
    }
  };

  return (
    <div className="group animate-in fade-in zoom-in-95 duration-200">
      <div className="flex items-start gap-4">
        <Link to={`/profile/${comment.user_id}`} className="flex-shrink-0">
            <img
            src={comment.user_picture}
            alt={comment.user_name}
            className="w-10 h-10 rounded-full border border-white/10 shadow-sm hover:border-primary transition-colors"
            />
        </Link>
        
        <div className="flex-1 bg-card/40 border border-white/5 rounded-2xl p-4 hover:border-white/10 transition-colors">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
                <Link to={`/profile/${comment.user_id}`} className="font-bold text-sm text-foreground/90 hover:text-primary transition-colors">
                  {comment.user_name}
                </Link>
                <span className="text-xs text-muted-foreground">•</span>
                <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                </span>
            </div>
          </div>

          <p className="text-foreground/80 leading-relaxed text-[15px] mb-3 whitespace-pre-wrap">{comment.content}</p>

          <div className="flex items-center gap-4">
            <div className="flex items-center bg-background/50 rounded-full border border-white/5 p-1">
                <button 
                    onClick={() => handleVote('up')}
                    className={`p-1 rounded-full transition-colors ${voteStatus === 'up' ? 'text-green-400 bg-green-400/10' : 'text-muted-foreground hover:text-green-400 hover:bg-white/5'}`}
                >
                    <ArrowBigUp className={`w-5 h-5 ${voteStatus === 'up' ? 'fill-current' : ''}`} />
                </button>
                <span className={`text-xs font-bold w-6 text-center ${voteStatus === 'up' ? 'text-green-400' : voteStatus === 'down' ? 'text-red-400' : 'text-muted-foreground'}`}>
                    {score}
                </span>
                <button 
                    onClick={() => handleVote('down')}
                    className={`p-1 rounded-full transition-colors ${voteStatus === 'down' ? 'text-red-400 bg-red-400/10' : 'text-muted-foreground hover:text-red-400 hover:bg-white/5'}`}
                >
                    <ArrowBigDown className={`w-5 h-5 ${voteStatus === 'down' ? 'fill-current' : ''}`} />
                </button>
            </div>

            <button
              onClick={() => setShowReply(!showReply)}
              className="text-xs font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-white/5"
            >
              <MessageCircle className="w-4 h-4" />
              Reply
            </button>
          </div>

          {showReply && (
            <form onSubmit={handleReply} className="mt-4 flex gap-2 animate-in slide-in-from-top-2 duration-200">
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                autoFocus
                placeholder="Write a reply..."
                className="flex-1 px-4 py-2 bg-background border border-border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-muted-foreground/50"
              />
              <button
                type="submit"
                disabled={posting || !replyText.trim()}
                className="w-10 h-10 flex items-center justify-center bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
